import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import reportService from '../../services/reportService';

// Icons
import { Flag, CheckCircle, XCircle, Clock, Search } from 'lucide-react';

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const AdminReports = () => {
  const { currentUser } = useAuth();
  const { showError, handleAsyncOperation } = useFeedback();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, resolved: 0, dismissed: 0 });
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [resolveModal, setResolveModal] = useState(null);
  const [resolutionText, setResolutionText] = useState('');

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null);

        const [reportsData, reportStats] = await Promise.all([
          reportService.getAllReports(),
          reportService.getReportStats(),
        ]);

        setReports(reportsData);
        setStats({
          total: reportStats.total,
          pending: reportStats.pending,
          resolved: reportStats.resolved,
          dismissed: reportStats.dismissed,
        });
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError('Failed to load reports. Please try again.');
        showError('Error', 'Failed to load reports.');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [currentUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtered reports based on status and search
  const filteredReports = reports.filter((report) => {
    const matchesStatus = filterStatus === 'all' || report.status === filterStatus;
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      !searchTerm ||
      (report.reason || '').toLowerCase().includes(searchLower) ||
      (report.description || '').toLowerCase().includes(searchLower) ||
      (report.targetType || '').toLowerCase().includes(searchLower);
    return matchesStatus && matchesSearch;
  });

  const handleResolve = async () => {
    if (!resolveModal) return;
    if (!resolutionText.trim()) {
      showError('Validation Error', 'Please provide a resolution note.');
      return;
    }

    const reportId = resolveModal.id;
    setActionLoading(reportId);
    try {
      await handleAsyncOperation(
        async () => {
          await reportService.updateReportStatus(reportId, 'resolved', resolutionText.trim(), currentUser?.uid);
        },
        { title: 'Resolving report...', message: 'Please wait while we update the report status.' },
        { title: 'Report Resolved', message: 'The report has been marked as resolved.' },
        { title: 'Error', message: 'Failed to resolve report. Please try again.' }
      );

      // Update local state
      setReports((prev) =>
        prev.map((r) =>
          r.id === reportId
            ? { ...r, status: 'resolved', resolution: resolutionText.trim(), resolvedBy: currentUser?.uid, resolvedAt: new Date() }
            : r
        )
      );
      setStats((prev) => ({
        ...prev,
        pending: Math.max(0, prev.pending - 1),
        resolved: prev.resolved + 1,
      }));

      // Close modal
      setResolveModal(null);
      setResolutionText('');
    } catch (err) {
      console.error('Error resolving report:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = async (reportId) => {
    if (!window.confirm('Are you sure you want to dismiss this report?')) return;

    setActionLoading(reportId);
    try {
      await handleAsyncOperation(
        async () => {
          await reportService.updateReportStatus(reportId, 'dismissed', 'Dismissed by admin', currentUser?.uid);
        },
        { title: 'Dismissing report...', message: 'Please wait while we update the report status.' },
        { title: 'Report Dismissed', message: 'The report has been dismissed.' },
        { title: 'Error', message: 'Failed to dismiss report. Please try again.' }
      );

      // Update local state
      setReports((prev) =>
        prev.map((r) =>
          r.id === reportId
            ? { ...r, status: 'dismissed', resolution: 'Dismissed by admin', resolvedBy: currentUser?.uid, resolvedAt: new Date() }
            : r
        )
      );
      setStats((prev) => ({
        ...prev,
        pending: Math.max(0, prev.pending - 1),
        dismissed: prev.dismissed + 1,
      }));
    } catch (err) {
      console.error('Error dismissing report:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Reports Management</h2>
          <p className="text-gray-600">Review and manage flagged content</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-1">Something went wrong</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Reports Management</h2>
        <p className="text-gray-600">Review and manage flagged content</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total</p>
            <Flag className="h-5 w-5 text-gray-400" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-gray-900">{stats.total}</span>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Pending</p>
            <Clock className="h-5 w-5 text-amber-400" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-amber-600">{stats.pending}</span>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Resolved</p>
            <CheckCircle className="h-5 w-5 text-green-400" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-green-600">{stats.resolved}</span>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Dismissed</p>
            <XCircle className="h-5 w-5 text-gray-400" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold text-gray-600">{stats.dismissed}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by reason, description, or target..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          {/* Filter by status */}
          <div className="sm:w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reporter</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredReports.length > 0 ? (
                filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{report.reporterId ? report.reporterId.substring(0, 8) + '...' : 'Anonymous'}</div>
                      <div className="text-xs text-gray-400 capitalize">{report.reportType || 'unknown'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">{report.targetType || 'unknown'}</div>
                      <div className="text-xs text-gray-400">{report.targetId ? report.targetId.substring(0, 8) + '...' : 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700 capitalize">{report.reason || 'other'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate" title={report.description}>
                        {report.description || 'No description provided'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        report.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                        report.status === 'resolved' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {report.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{formatDate(report.createdAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        {report.status === 'pending' && (
                          <>
                            <button
                              onClick={() => {
                                setResolveModal(report);
                                setResolutionText('');
                              }}
                              disabled={actionLoading === report.id}
                              className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 transition-colors disabled:opacity-50"
                              title="Resolve"
                            >
                              <CheckCircle className="h-3.5 w-3.5 mr-1" />
                              Resolve
                            </button>
                            <button
                              onClick={() => handleDismiss(report.id)}
                              disabled={actionLoading === report.id}
                              className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
                              title="Dismiss"
                            >
                              <XCircle className="h-3.5 w-3.5 mr-1" />
                              Dismiss
                            </button>
                          </>
                        )}
                        {report.status !== 'pending' && report.resolution && (
                          <span className="text-xs text-gray-400 italic max-w-[120px] truncate" title={report.resolution}>
                            {report.resolution}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <Flag className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No reports found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Count */}
      <div className="mt-4 text-sm text-gray-500">
        Showing {filteredReports.length} of {reports.length} reports
      </div>

      {/* Resolve Modal */}
      {resolveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">Resolve Report</h3>
              <p className="text-sm text-gray-500 mt-1">Provide a resolution note explaining the outcome of this report.</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Resolution Note</label>
              <textarea
                value={resolutionText}
                onChange={(e) => setResolutionText(e.target.value)}
                rows={4}
                placeholder="e.g. Reviewed the flagged content and removed the violating post. Warning issued to the user."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setResolveModal(null);
                  setResolutionText('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResolve}
                disabled={actionLoading === resolveModal.id}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50"
              >
                {actionLoading === resolveModal.id ? 'Resolving...' : 'Confirm Resolve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
